import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Cta, Hero, Para, Question, Section, Steps, linkStyle } from './Landing';

const PlayWithFriends = () => (
    <Box>
        <Helmet>
            <title>Play Sequence Online With Friends – Free, With Video Calls | Play Sequence</title>
            <meta name="description" content="Play Sequence online with friends for free. Send a link, play 2 to 12 players in teams, and see each other on the built-in video call. No sign-up, no download." />
            <meta property="og:title" content="Play Sequence Online With Friends – Free, With Video Calls" />
            <meta property="og:description" content="Send a link, pick teams and play Sequence together, with video calls and chat built in. Free, no sign-up." />
        </Helmet>

        <Hero
            title="Play Sequence Online With Friends"
            subtitle="Send a link, pick teams and play together, face to face on the built-in video call."
        />

        <Container maxWidth="md" sx={{ py: 6 }}>
            <Section title="Set up a game night in a minute">
                <Steps ordered items={[
                    <>Open <Link to="/" style={linkStyle}>play-sequence.com</Link> and choose <strong>Play with friends</strong>.</>,
                    <>Name the room and pick how many are playing: 2, 3, 4, 6, 8, 9, 10 or 12.</>,
                    <>Share the link however you like: a message, an email or your group chat. Friends open it and join, with no account and nothing to install.</>,
                    <>Pick teams once everyone is in, and start.</>,
                ]} />
                <Para last>
                    Just one friend? <strong>Challenge a friend</strong> makes a one-on-one game that starts the moment they open the link. And if nobody's free, <strong>Find an opponent</strong> matches you with another player online, or you can <Link to="/play-against-computer" style={linkStyle}>play against the computer</Link>.
                </Para>
            </Section>

            <Section title="Video calls and chat, built in">
                <Para>
                    Sequence is best around a table, so the table comes with a call. Join the video call from the game and see and hear the people you're playing with, up to five on camera at once. There's nothing else to set up: no separate meeting link, no app.
                </Para>
                <Para last>
                    Prefer to type? The chat sits beside the board for everyone in the game, with a badge so you never miss a message.
                </Para>
            </Section>

            <Section title="Teams of 2 or 3">
                <Para>
                    With two teams you need two sequences to win; with three teams, one is enough. Team-mates share a colour and take turns in between the other teams, so a 4-player game is two against two and a 6-player game can be three teams of two.
                </Para>
                <Para last>
                    Set it up how your group likes to play: the <strong>classic board</strong> or a <strong>shuffled board</strong> that's new every game, an optional <strong>turn timer</strong> of 30 seconds, 1 minute or 2 minutes, and <strong>highlights</strong> on or off for everyone. When it's over, a rematch is one click away.
                </Para>
            </Section>

            <Section title="Questions">
                <Question q="Is it free to play Sequence online with friends?">
                    Yes. Creating a room, inviting friends and the video call are all free, with no accounts or downloads for anyone.
                </Question>
                <Question q="How many people can play?">
                    2 to 12 players in a room, split into 2 or 3 teams of equal size. The video call holds up to five people at once.
                </Question>
                <Question q="Can we play on phones?">
                    The board needs a wider screen, so each player needs a computer, a laptop or a tablet turned sideways.
                </Question>
                <Question q="Do my friends need the rules first?">
                    No, the game enforces them. If someone wants a head start, share <Link to="/how-to-play" style={linkStyle}>how to play Sequence</Link>.
                </Question>
            </Section>

            <Cta title="Get the group together" text="Make a room, send the link and play Sequence together." />
        </Container>
    </Box>
);

export default PlayWithFriends;
